import type { ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { useUserRole } from '@/hooks/useUserRole';
import logoWhite from '@/assets/obriens-logo-white.png';
import '@/styles/screens.css';

/**
 * Sits inside the AuthGuard: the user is signed in, but we still need their
 * profile (GET /users/me) before showing the app. Handles three in-between
 * states so nobody lands in a broken-looking empty shell:
 *   - loading    → a neutral spinner (no "Signing in…" flash)
 *   - load error → a retry screen
 *   - provisioned but not linked to a store (a new `store` user with no stores)
 *                → a friendly "ask an admin" screen
 */
export function ProfileGate({ children }: { children: ReactNode }) {
  const { instance } = useMsal();
  const { loading, loadError, isStore, stores } = useUserRole();

  if (loading) {
    return (
      <div className="auth-splash">
        <span className="spin" aria-label="Loading">⟳</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <CenteredCard hint="We couldn’t load your profile. Please try again.">
        <button type="button" className="btn btn--primary login-btn" onClick={() => window.location.reload()}>
          Retry
        </button>
      </CenteredCard>
    );
  }

  // Signed in + provisioned, but a store user with no store assigned yet.
  if (isStore && stores.length === 0) {
    return (
      <CenteredCard hint="You’re signed in, but you’re not linked to a store yet. Ask an administrator to set you up, then sign in again.">
        <button type="button" className="btn btn--ghost login-btn" onClick={() => instance.logoutRedirect().catch(console.error)}>
          Sign out
        </button>
      </CenteredCard>
    );
  }

  return <>{children}</>;
}

function CenteredCard({ hint, children }: { hint: string; children: ReactNode }) {
  return (
    <div className="auth-splash">
      <div className="login-card">
        <div className="login-logo"><img src={logoWhite} alt="O'Briens" /></div>
        <div className="login-body">
          <p className="login-hint">{hint}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
